/**
 * UserMenu Template Component
 *
 * A comprehensive user menu with navigation, social links, and points display.
 * Displayed in a drawer/sidebar when user clicks their avatar.
 *
 * @module @ideoz/ui/components/templates/UserMenu
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserMenuProps } from '../../types/UserMenu.types';
import { Heart, X } from 'lucide-react';
import { Badge } from '../atoms/badge';
import { Button, cn } from '@ideoz/ui';
import { useAppSelector, selectUser, openWebBillingWithToken, isPlanDetailsVisible } from '@ideoz/core';
import { motion } from 'motion/react';
import Icon from '../molecules/Icon';

/**
 * UserMenu Template
 *
 * Displays user navigation, social links, and points information.
 * Pure presentation component - all business logic handled by wrappers.
 *
 * @example
 * ```tsx
 * <UserMenuTemplate
 *   onClose={() => setMenuOpen(false)}
 *   points={1250}
 *   pointsLabel="ideoz_AI_Points"
 *   menuItems={[
 *     { id: 'profile', label: 'Profile', icon: User, onClick: handleProfile },
 *     { id: 'help', label: 'Help', icon: HelpCircle, href: 'https://...', isExternal: true }
 *   ]}
 *   socialLinks={socialLinksArray}
 *   onRequestUpgrade={handleRequestUpgrade}
 *   isWeb={true}
 * />
 * ```
 */
export const UserMenuTemplate: React.FC<UserMenuProps> = ({
  onClose,
  points = 0,
  pointsLabel = 'Points',
  menuItems,
  socialLinks = [],
  onRequestUpgrade,
  isRequestingUpgrade = false,
  canRequestUpgrade = false,
  isWeb,
  className = '',
  testId = 'user-menu',
}) => {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const planDetails = useAppSelector((state: any) => state.app.planDetails);
  const userName = user?.name || 'User';
  const currentPlanName = planDetails?.name || 'Free';
  const showPlanDetails = isPlanDetailsVisible();
  const pointsPercentage = Math.min(points, 100);
  const MENU_ITEM_BASE_CLASSES =
    'min-w-10 min-h-10 px-2 group relative flex items-center gap-2 py-2 rounded-lg cursor-pointer transition-all duration-150 ease-out text-gray-700 dark:text-gray-300 hover:bg-blue-light-100/80 hover:text-blue-light-600 dark:hover:bg-blue-light-900/30 dark:hover:text-blue-light-300';

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.1, ease: 'easeInOut' }}
      className={`bg-surface-2 border-e border-gray-100 dark:border-gray-800 flex flex-col gap-6 p-6 absolute top-0 bottom-0 ${className}`}
      data-testid={testId}
    >
      <header className="pt-4 flex gap-2 justify-between items-center relative">
        <h2 className="text-lg lg:text-2xl font-semibold text-gray-600 dark:text-gray-200">
          {t('userMenu.greeting', { name: userName.split(' ')[0] })}
        </h2>
        <Button
          className=""
          variant="ghost"
          size="xs"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <Icon icon={X} size={20} />
        </Button>
      </header>
      {showPlanDetails && (
      <div className="w-full flex items-center justify-between">
        <Badge
          variant="secondary"
          size="xs"
          className="text-xs hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700"
        >
          {t('userMenu.currentPlan', { plan: currentPlanName })}
        </Badge>
        {currentPlanName == 'Free' &&
          (isWeb ? (
            <a href="/billing" className="">
              <Badge
                variant="primary"
                size="xs"
                className="bg-primary-700 hover:bg-primary-800 text-white hover:text-white text-xs border-none"
              >
                {t('userMenu.upgrade')}
              </Badge>
            </a>
          ) : (
            <button onClick={openWebBillingWithToken} className="">
              <Badge
                variant="primary"
                size="xs"
                className="bg-primary-700 hover:bg-primary-800 text-white hover:text-white text-xs border-none"
              >
                {t('userMenu.upgrade')}
              </Badge>
            </button>
          ))}
      </div>
      )}
      {/* Navigation Menu */}
      <nav className="flex flex-col gap-2">
        {menuItems.map((item) => {
          const ItemIcon = item.icon;

          if (item.href) {
            return (
              <a
                key={item.id}
                className={cn(
                  MENU_ITEM_BASE_CLASSES,
                  item.isActive
                    ? 'bg-blue-light-100/80 text-blue-light-600 dark:bg-blue-light-900/40 dark:text-blue-light-300'
                    : ''
                )}
                href={item.href}
                target={item.target || '_blank'}
                rel={item.isExternal ? 'noopener noreferrer' : undefined}
                onClick={item.onClick}
                data-testid={item.testId || `${testId}-item-${item.id}`}
              >
                <ItemIcon size={20} />
                <span className="text-sm">
                  {t(`userMenu.items.${item.id}`, { defaultValue: item.label })}
                </span>
              </a>
            );
          }

          return (
            <button
              key={item.id}
              className={cn(
                MENU_ITEM_BASE_CLASSES,
                item.isActive
                  ? 'bg-blue-light-100/80 text-blue-light-600 dark:bg-blue-light-900/40 dark:text-blue-light-300'
                  : ''
              )}
              onClick={item.onClick}
              data-testid={item.testId || `${testId}-item-${item.id}`}
            >
              <ItemIcon size={20} />
              <span className="text-sm">
                {t(`userMenu.items.${item.id}`, { defaultValue: item.label })}
              </span>
            </button>
          );
        })}
      </nav>

      {/* Social Links Section */}
      {socialLinks.length > 0 && (
        <div className="mt-20 px-4">
          <div className="flex items-center gap-3 mt-80 ms-14">
            {socialLinks.map((social) => (
              <a
                key={social.id}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                data-testid={social.testId || `${testId}-social-${social.id}`}
                aria-label={social.name}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Points Section */}
      {/* {points !== undefined && (
        <div className="px-4">
          <div className="bg-gray-100 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <h6 className="text-base font-semibold text-gray-900">{pointsLabel}</h6>
            </div>

            <p className="text-sm text-gray-600 mb-3">
              You have <span className="font-bold">{Number(points).toFixed(0)} Points</span> points
              remaining to use in generating experience every month
            </p>

            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div
                className="bg-secondary-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${pointsPercentage}%` }}
              />
            </div>

            {onRequestUpgrade && (
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isRequestingUpgrade || !canRequestUpgrade}
                  onClick={onRequestUpgrade}
                  className="text-primary-600 hover:text-primary-700 p-0 h-auto font-medium"
                  data-testid={`${testId}-request-upgrade-btn`}
                >
                  Gain More Points
                  {isRequestingUpgrade && '...'}
                </Button>
              </div>
            )}
          </div>
        </div>
      )} */}

      {/* Footer */}
      <footer
        className="pt-4 border-t border-gray-200 dark:border-gray-800 mt-auto flex justify-center w-full items-center gap-1 text-xs text-gray-600 dark:text-gray-400 font-regular"
        data-testid="footer"
      >
        <span>{t('userMenu.buildWith')}</span>
        <Heart
          size={14}
          strokeWidth={2}
          className="text-[#fd5353] fill-[#fd5353]"
          aria-label="love"
        />
        <span>{t('userMenu.fromMena')}</span>
      </footer>
    </motion.div>
  );
};

export default UserMenuTemplate;
